import * as React from 'react';
import lune from 'lune';
import { authedClient } from '../../api/authedClient';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import ButtonGroup from '@mui/material/ButtonGroup';
import Typography from '@mui/material/Typography';

interface DateSelectorComponentProps {
  selectedDate: Date;
  setSelectedDate: (value: Date) => void;
}

const DateSelectorComponent: React.FC<DateSelectorComponentProps> = ({
  selectedDate,
  setSelectedDate,
}) => {
  const [futureDateOnWidget, setFutureDateOnWidget] = React.useState<Date>(new Date());
  const [moonPhaseData, setMoonPhaseData] = React.useState<{ [key: string]: string }>({});
  const canvasRef = React.useRef<HTMLDivElement | null>(null);
  const dateToday = new Date();
  dateToday.setHours(0, 0, 0, 0);

  const highTideDays = [
    new Date('2025-01-17'),
    new Date('2025-02-03'),
    new Date('2025-03-04'),
    dateToday,
    futureDateOnWidget,
  ];

  const daysToDisplay = [...highTideDays];

  const shadowSizeMapping: { [key: string]: number } = {
    'New Moon': 0,
    'Waxing Crescent': 0.25,
    'First Quarter': 0.5,
    'Waxing Gibbous': 0.75,
    'Full Moon': 1.0,
    'Waning Gibbous': 0.75,
    'Last Quarter': 0.5,
    'Waning Crescent': 0.25,
  };

  // ---------------- Fetch Future Date ----------------
  const fetchFutureDateOnWidget = async () => {
    const endPoint = '/api/map/predicted-data/date-on-max-level';
    try {
      const response = await authedClient.get(endPoint);
      setFutureDateOnWidget(new Date(response.data.timestamp));
    } catch {
      setFutureDateOnWidget(new Date());
    }
  };

  // ---------------- Local Moon Phase Calculation (using lune) ----------------
  const fetchMoonPhaseData = async () => {
    const phaseData: { [key: string]: string } = {};

    daysToDisplay.forEach((date) => {
      const { phase } = lune.phase(date); // 'phase' is a number from 0–1
      const phaseName = getPhaseName(phase);
      phaseData[date.toDateString()] = phaseName;
    });

    console.log("phaseData");
    console.log(phaseData);
    setMoonPhaseData(phaseData);
  };

  const getPhaseName = (moonPhase: number): string => {
    if (moonPhase < 0.03 || moonPhase > 0.97) return 'New Moon';
    if (moonPhase < 0.22) return 'Waxing Crescent';
    if (moonPhase < 0.28) return 'First Quarter';
    if (moonPhase < 0.47) return 'Waxing Gibbous';
    if (moonPhase < 0.53) return 'Full Moon';
    if (moonPhase < 0.72) return 'Waning Gibbous';
    if (moonPhase < 0.78) return 'Last Quarter';
    return 'Waning Crescent';
  };

  const handleMoonPhaseSelection = (date: Date) => {
    setSelectedDate(date);
    if (canvasRef.current) {
      const phase = moonPhaseData[date.toDateString()];
      const shadowSize = shadowSizeMapping[phase] || 0;
      const isWaxing = phase.includes('Waxing');
      if (window.drawPlanetPhase) {
        canvasRef.current.innerHTML = '';
        window.drawPlanetPhase(canvasRef.current, shadowSize, isWaxing);
      } else {
        console.error('drawPlanetPhase is not defined on the window object.');
      }
    }
  };

  React.useEffect(() => {
    fetchFutureDateOnWidget();
    fetchMoonPhaseData();
  }, []);

  React.useEffect(() => {
    if (typeof window.drawPlanetPhase !== 'function') {
      console.error('drawPlanetPhase is not loaded!');
    } else {
      console.log('drawPlanetPhase is loaded and ready to use.');
    }
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '& > *': {
          m: 1,
        },
      }}
    >
      <ButtonGroup
        variant="contained"
        color="primary"
        aria-label="Medium-sized button group"
        sx={{
          '& .MuiButton-contained': {
            backgroundColor: 'rgba(47, 47, 47, 0.88)',
            '&:hover': {
              backgroundColor: '#232324',
            },
            '&:not(:last-child)': {
              borderRight: '1px solid #232324',
            },
          },
        }}
      >
        {daysToDisplay.map((date, index) => {
          const isToday = date.toDateString() === new Date().toDateString();
          const formattedDate = date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          });
          const dayOfWeek = date.toLocaleDateString('en-US', {
            weekday: 'short',
          });

          return (
            <Button
              key={index}
              onClick={() => handleMoonPhaseSelection(date)}
              sx={{
                fontSize: 'text-base',
                fontFamily: 'Inter',
                textTransform: 'none',
                backgroundColor:
                  selectedDate.toDateString() === date.toDateString()
                    ? 'rgb(24, 24, 24, 0.9) !important'
                    : undefined,
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  margin: '5px',
                }}
                ref={(el) => {
                  if (el && moonPhaseData[date.toDateString()]) {
                    const phase = moonPhaseData[date.toDateString()];
                    const shadowSize = shadowSizeMapping[phase] || 0;
                    const isWaxing = phase.includes('Waxing');
                    el.innerHTML = '';
                    window.drawPlanetPhase(el, shadowSize, isWaxing, {
                      diameter: 40,
                      shadowColour: 'rgb(51, 51, 51, 1)',
                      lightColour: '#fffec8',
                      blur: 3,
                    });
                  }
                }}
              />
              {isToday ? (
                <>
                  <Typography
                    sx={{ fontSize: '14px', lineHeight: '1.2', mb: 0.2, color: '#fff' }}
                  >
                    Today
                  </Typography>
                  <Typography sx={{ fontSize: '10px', lineHeight: '1.2', color: '#bbb' }}>
                    {formattedDate}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    sx={{ fontSize: '14px', lineHeight: '1.2', mb: 0.2, color: '#fff' }}
                  >
                    {formattedDate}
                  </Typography>
                  <Typography sx={{ fontSize: '10px', lineHeight: '1.2', color: '#bbb' }}>
                    {dayOfWeek}
                  </Typography>
                </>
              )}
            </Button>
          );
        })}
      </ButtonGroup>
    </Box>
  );
};

export default DateSelectorComponent;
